import { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ClientHeader } from "@/components/ClientHeader";
import { AddCategoryDialog } from "@/components/AddCategoryDialog";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { Eye, EyeOff, FileDown, FileText, Loader2, Plus, UserPlus } from "lucide-react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import type { Client, InsertClientWithExecutors, ClientHiddenCategory } from "@shared/schema";
import { getIcon } from "@/hooks/use-categories";
import { EstateEssentialsCard } from "@/components/EstateEssentialsCard";
import { EstateSnapshotCard } from "@/components/EstateSnapshotCard";
import { JourneyStageStrip } from "@/components/JourneyStageStrip";
import { NextUpCard } from "@/components/NextUpCard";
import { ExecutorNeedsPanel } from "@/components/ExecutorNeedsPanel";
import { SecureFileStatusCard } from "@/components/SecureFileStatusCard";
import { BuildFromDocumentsCard } from "@/components/BuildFromDocumentsCard";
import { BatchUploadModal } from "@/components/BatchUploadModal";
import { useDashboardProgress } from "@/hooks/useDashboardProgress";
import { useEstateReadyScore } from "@/hooks/useEstateReadyScore";
import { Skeleton } from "@/components/ui/skeleton";
import { CATEGORY_SLUGS } from '@shared/constants/categories';
import { EXECUTOR_TERM } from '@shared/constants/terminology';
import {
  EXECUTOR_NEEDS_TITLE,
  isExecutorNeedsPanelVisible,
} from '@shared/constants/executor-needs-panel';
import { WARM } from "@shared/brand";

interface DashboardProps {
  clientData: Client | null;
  readOnly?: boolean;
  onChangePassword?: () => void;
  showClientHeader?: boolean;
}

interface UnifiedCategoryProgress {
  id: string;
  slug: string;
  name: string;
  icon: string;
  categoryType: string;
  sortOrder: number;
  completed: number;
  total: number;
}

interface CategoryWithProgress {
  id: string;
  slug: string;
  title: string;
  icon: string;
  completed: number;
  total: number;
}

export default function Dashboard({ clientData, readOnly = false, onChangePassword, showClientHeader = true }: DashboardProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  // s98 Build from Documents — batch upload modal open state. The card sets it; the
  // modal itself is wired in the follow-up commit.
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<CategoryWithProgress | null>(null);
  const [hideCategoryTarget, setHideCategoryTarget] = useState<CategoryWithProgress | null>(null);

  const updateClientMutation = useMutation({
    mutationFn: async (data: InsertClientWithExecutors) => {
      if (!clientData?.id) throw new Error("No client ID");
      const res = await apiRequest("PATCH", `/api/clients/${clientData.id}`, data);
      return await res.json() as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id] });
      toast({
        title: "Contact information updated",
        description: "Your contact details have been successfully updated.",
      });
      setEditClientOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating contact information",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Fetch unified category progress from the new API
  const { data: unifiedProgress = [] } = useQuery<UnifiedCategoryProgress[]>({
    queryKey: ["/api/unified/progress", clientData?.id],
    queryFn: async () => {
      const res = await fetch(`/api/unified/progress?clientId=${clientData?.id}`);
      if (!res.ok) throw new Error("Failed to fetch progress");
      return res.json();
    },
    enabled: !!clientData?.id,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // F&F Estate Ready Score — only fetched for F&F testers.
  // Wizard progress — used for isPlanComplete detection.
  // Same key as WizardProgressCard; TanStack Query deduplicates the request.
  const { data: wizard } = useQuery<WizardProgressData>({
    queryKey: ["/api/wizard/progress"],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: !readOnly && !!clientData,
  });

  // Fetch hidden categories for this client
  const { data: hiddenCategories = [] } = useQuery<ClientHiddenCategory[]>({
    queryKey: ["/api/clients", clientData?.id, "hidden-categories"],
    queryFn: async () => {
      if (!clientData?.id) return [];
      const res = await fetch(`/api/clients/${clientData.id}/hidden-categories`);
      if (!res.ok) throw new Error("Failed to fetch hidden categories");
      return res.json();
    },
    enabled: !!clientData?.id,
  });

  const hiddenCategoryIds = useMemo(() => {
    return new Set(hiddenCategories.map(hc => hc.categoryId));
  }, [hiddenCategories]);

  // Transform unified progress to the CategoryWithProgress format (excluding hidden ones)
  const categoriesWithProgress: CategoryWithProgress[] = useMemo(() => {
    return unifiedProgress
      .filter(category => !hiddenCategoryIds.has(category.id))
      .map(category => ({
        id: category.id,
        slug: category.slug,
        title: category.name,
        icon: category.icon,
        completed: category.completed,
        total: category.total,
      }));
  }, [unifiedProgress, hiddenCategoryIds]);

  // Map for all categories (including hidden) for display purposes
  const allCategoriesMap = useMemo(() => {
    const map = new Map<string, UnifiedCategoryProgress>();
    unifiedProgress.forEach(cat => map.set(cat.id, cat));
    return map;
  }, [unifiedProgress]);

  // Is this plan complete? 🔴 s178: this NO LONGER changes what renders — the celebration takeover
  // is retired and the dashboard always shows. Its only consumer is the silent `planCompletedAt`
  // stamp (see the effect below).
  // NOTE (s80), SUPERSEDED s178 — kept because the supersession is the point, not the note.
  // s80 recorded that this gate was wizard-derived and would "re-key to the Ten Essentials
  // (essentials.score === total) when the wizard is deleted at P3". 🔴 That is NOT what happened,
  // and Ruling A (s178) says why: the note predates the ONE-BAR ruling (D-156.10), and the banner's
  // own copy — "Every section is filled in" — makes an every-SECTION claim that only the section bar
  // can honestly back. Essentials are a 10-item rollup; they can read 10/10 while sections remain
  // unfinished, so keying the banner to them would have promised something the number never checked.
  //
  // 🔴 s178 (CONSISTENCY-PASS-1 member c): `planComplete` is now computed SERVER-side over every
  // scored section and read straight off the payload. It is deliberately no longer inferred from
  // `stages.every(...)`: the stages only cover sections carrying a `wizardStage`, so that inference
  // claimed "every section" while silently ignoring every un-staged one.
  const isPlanComplete =
    !readOnly &&
    !!clientData &&
    !!wizard &&
    wizard.planComplete === true;

  const recordCompletionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/clients/${clientData!.id}/plan-completed`);
      return await res.json() as { planCompletedAt: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id] });
    },
  });

  // 🔴 s178 — THE STAMP STAYS, AND IT IS NOW SILENT. `clients.plan_completed_at` records the moment
  // a plan first became complete. With the takeover screen retired it has **ZERO UI readers** — by
  // ruling, not by oversight, so do not "clean up" the write as dead code.
  //
  // It is kept as a business fact for the future Annual Estate Review: the review needs to know when
  // a plan was last considered done, and that moment is unrecoverable after the fact — nothing else
  // in the schema records it. Writing it now costs one row-update; not writing it loses the date
  // permanently.
  //
  // Write-once by construction: guarded here on `!planCompletedAt`, and the route itself returns the
  // existing value rather than overwriting (verified on staging s178 — three calls, one timestamp).
  useEffect(() => {
    if (isPlanComplete && clientData && !clientData.planCompletedAt) {
      recordCompletionMutation.mutate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlanComplete, clientData?.id]);

  const addCategoryMutation = useMutation({
    mutationFn: async (data: { title: string; icon: string }) => {
      const slug = data.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const res = await apiRequest("POST", "/api/generic-categories", {
        name: data.title,
        slug,
        icon: data.icon,
        categoryType: "custom",
        isDefaultTemplate: false,
        isActive: true,
        clientId: clientData?.id || null,
      });
      return await res.json();
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      toast({
        title: "Category added",
        description: `"${data.title}" has been added to your categories.`,
      });
      setAddCategoryOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire mints a custom category for this client.
  const addCategoryFlight = useSingleFlight();

  const handleAddCategory = (data: { title: string; icon: string }) => {
    addCategoryFlight.runQuiet(() => addCategoryMutation.mutateAsync(data));
  };

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/generic-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      setDeleteCategoryTarget(null);
      toast({
        title: "Category deleted",
        description: "The category has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDeleteCategory = (id: string) => {
    const category = categoriesWithProgress.find(c => c.id === id);
    if (category) setDeleteCategoryTarget(category);
  };

  const hideCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!clientData?.id) throw new Error("No client ID");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/hidden-categories`, { categoryId });
      return await res.json();
    },
    onSuccess: (_, categoryId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "hidden-categories"] });
      const category = allCategoriesMap.get(categoryId);
      setHideCategoryTarget(null);
      toast({
        title: "Category hidden",
        description: `"${category?.name || categoryId}" has been hidden from your dashboard.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error hiding category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleHideCategory = (categoryId: string) => {
    const category = categoriesWithProgress.find(c => c.id === categoryId);
    if (category) setHideCategoryTarget(category);
  };

  const unhideCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!clientData?.id) throw new Error("No client ID");
      await apiRequest("DELETE", `/api/clients/${clientData.id}/hidden-categories/${categoryId}`);
    },
    onSuccess: (_, categoryId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "hidden-categories"] });
      const category = allCategoriesMap.get(categoryId);
      toast({
        title: "Category restored",
        description: `"${category?.name || categoryId}" is now visible on your dashboard.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error restoring category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleUnhideCategory = (categoryId: string) => {
    unhideCategoryMutation.mutate(categoryId);
  };

  // s83 security fix: category reorder is GLOBAL template state and now admin-only
  // (server requireRole). The planner Dashboard never rendered a draggable category
  // list — the vestigial reorder mutation/handler that targeted the global endpoint
  // were removed. Category order is owned by the admin console (UnifiedCategories).

  // Government forms readiness — only fetch for executor (readOnly) view
  const { data: formsExportData } = useQuery<{
    summary: { totalFields: number; filledFields: number; missingCount: number };
    forms: Array<{ title: string; fields: Array<{ value: string | null }> }>;
  }>({
    queryKey: ["/api/clients", clientData?.id, "export", "ontario-forms"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData!.id}/export/ontario-forms`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!clientData?.id && readOnly,
    staleTime: 0,
  });

  // s99 Bug-2 (dashboard partial-render): the working-area cards (Ten Essentials,
  // Next Up, the journey strip, Talk-to-Executor, Estate Snapshot) each fetch their
  // own data and previously returned null while loading, so they popped in one at a
  // time — a partial-render flash. Hoist the shared underlying queries here so they
  // start fetching at Dashboard mount, gate the whole working area behind a single
  // loading flag, and render a skeleton until the data has resolved; the cards then
  // mount together and reuse the now-cached data via TanStack key dedup (no extra
  // fetch — same keys/queryFns as the children). We read each query's own `isLoading`
  // (NOT data-presence), which flips false on error too, so a failed fetch falls
  // through to the cards' own empty states instead of latching the skeleton forever.
  const gateClientId = !readOnly && clientData ? clientData.id : undefined;
  const ersGateQuery = useEstateReadyScore(gateClientId);
  const overviewGateQuery = useQuery({
    queryKey: ["/api/clients", gateClientId, "section-overview"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${gateClientId}/section-overview`);
      if (!res.ok) throw new Error("Failed to load section overview");
      return res.json();
    },
    enabled: !!gateClientId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const entriesGateQuery = useQuery({
    queryKey: ["/api/clients", gateClientId, "generic-entries", "all"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${gateClientId}/generic-entries`);
      if (!res.ok) throw new Error("Failed to load entries");
      return res.json();
    },
    enabled: !!gateClientId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const summaryGateQuery = useQuery({
    queryKey: ["/api/clients", gateClientId, "estate-summary"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${gateClientId}/estate-summary`);
      if (!res.ok) throw new Error("Failed to fetch estate summary");
      return res.json();
    },
    enabled: !!gateClientId,
    staleTime: 60_000,
  });
  const workingAreaLoading =
    !!gateClientId &&
    (ersGateQuery.isLoading ||
      overviewGateQuery.isLoading ||
      entriesGateQuery.isLoading ||
      summaryGateQuery.isLoading);

  // Dashboard scroll restoration (s152 Item 3): return the planner to where they left (e.g. the gap
  // card) after clicking through to a field and back. The scroll lives on ClientDashboard's
  // `#app-scroll-main` container (not window). Save its position on unmount (nav away), restore once
  // the working area has rendered (restoring before content mounts would clamp to a short page).
  const SCROLL_KEY = "oe:dashboard-scroll";
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    // Save the position LIVE as the planner scrolls. An unmount-time read is unreliable: the useEffect
    // cleanup fires after the next route has already mounted in the shared #app-scroll-main container,
    // by which point scrollTop has reset to 0. A scroll listener always has the last real position.
    if (readOnly) return;
    const el = document.getElementById("app-scroll-main");
    if (!el) return;
    const save = () => sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
    el.addEventListener("scroll", save, { passive: true });
    return () => el.removeEventListener("scroll", save);
  }, [readOnly]);
  useEffect(() => {
    if (readOnly || workingAreaLoading || scrollRestoredRef.current) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved == null) return;
    scrollRestoredRef.current = true;
    const el = document.getElementById("app-scroll-main");
    if (el) requestAnimationFrame(() => { el.scrollTop = Number(saved); });
  }, [readOnly, workingAreaLoading]);

  const handleCategoryClick = (categoryId: string) => {
    const category = categoriesWithProgress.find(c => c.id === categoryId);
    if (category?.slug) {
      if (category.slug === CATEGORY_SLUGS.ONLINE_PRESENCE) {
        setLocation(`/category/online-presence`);
      } else {
        setLocation(`/category/${category.slug}`);
      }
    }
  };

  // Early return: PlanCompleteScreen — all wizard stages at 100%.
  // (s83: the pre-s80 WelcomeScreen early-return was removed — the standard
  // dashboard's three-stage journey + Start Here card handle the 0% case.)
  // 🔴 s178 — THE PLAN-COMPLETE TAKEOVER SCREEN IS RETIRED (Reade's ruling). The dashboard ALWAYS
  // renders; finishing your plan never replaces it. The early return that used to swap in
  // `PlanCompleteScreen` here is gone, and the component is deleted with it.
  //
  // Why it went, beyond taste: a takeover made the estate plan feel finished and closed, when the
  // honest state is a living document that will need revisiting. It also carried copy the product
  // could not keep — a "we'll email you in 12 months to review your plan" toast with no scheduler,
  // no job and no email behind it anywhere in `server/`.
  //
  // `isPlanComplete` survives and is still computed, because it drives the `planCompletedAt` stamp
  // below. See the note there: the stamp is now a SILENT business fact with zero UI readers.

  return (
    // s80 density: centered ~1100px content column. The stage strip, Talk-to-Executor,
    // and snapshot span this full width; essentials + next-up sit in a 2-col row below.
    <div className="p-6 space-y-6 max-w-[1100px] mx-auto">
      {showClientHeader && clientData && (
        <ClientHeader
          client={clientData}
          onEdit={readOnly ? undefined : () => setEditClientOpen(true)}
          onChangePassword={onChangePassword}
          readOnly={readOnly}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          {!readOnly && clientData && (
            <WelcomeHeading clientData={clientData} />
          )}
          <h1 className="text-3xl font-semibold tracking-tight">Your estate plan</h1>
          {!readOnly && clientData ? (
            <DashboardSubline clientData={clientData} />
          ) : (
            <p className="text-muted-foreground mt-1">Everything you're putting in place for your family. You're further along than most.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {clientData && !readOnly && user?.role !== 'client' && (
            <Link href="/export/ontario-forms">
              <Button variant="outline" className="gap-2" data-testid="button-ontario-export">
                <FileDown className="h-4 w-4" />
                Ontario Forms Export
              </Button>
            </Link>
          )}
          {!readOnly && user?.role !== 'client' && (
            <Button onClick={() => setAddCategoryOpen(true)} className="gap-2" data-testid="button-add-category">
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          )}
        </div>
      </div>

      {!clientData && (
        <Alert>
          <UserPlus className="h-4 w-4" />
          <AlertDescription>
            No client selected. Choose an existing client from the sidebar or create a new one.
          </AlertDescription>
        </Alert>
      )}

      {/* s99 Bug-2: the data-driven working area renders as a UNIT. While the shared
          queries (hoisted above) load, a skeleton stands in for the whole block; once
          they resolve, every card mounts together — no piecemeal pop-in. The cards
          reuse the now-cached data via TanStack key dedup. The batch-upload MODAL is
          rendered separately below so its open/close state is unaffected by the gate. */}
      {!readOnly && clientData && (
        workingAreaLoading ? (
          <DashboardWorkingAreaSkeleton />
        ) : (
          <>
            {/* Three-stage journey strip (s80) — full width, soft guidance at the top. */}
            <JourneyStageStrip clientData={clientData} />

            {/* SDE Phase 2 (s88): secure-file status — shows once setup is complete. */}
            <SecureFileStatusCard clientData={clientData} />

            {/* s98 Build from Documents — permanent batch-upload tool, above the essentials.
                Always shown (session "Not right now" aside). */}
            <BuildFromDocumentsCard onUploadClick={() => setBatchUploadOpen(true)} />

            {/* Working area (s80): Ten Essentials (left) + Complete-the-picture (right),
                equal-ish columns side by side; stacks vertically below ~900px viewport. */}
            <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-6 items-stretch">
              <EstateEssentialsCard clientId={clientData.id} />
              <NextUpCard clientData={clientData} />
            </div>

{/* PLANNER-LENS-1 (s152) / EXECUTOR-GAP-PANEL-2 (s199, D-199.14): "what your executor
                still needs" — the engine-computed gap list, full width below the working grid.
                Reframes completeness as executor-can-act.

                🔴 The panel is mounted HERE and nowhere else. D-199.11 shipped it as a card that
                listed every record with no inputs, above a link to a page that listed the same
                records again with the inputs — the planner read the identical list twice and only
                the second copy worked. D-199.14 reverses that: the page is gone, and this is where
                you type. Records stay collapsed with the first one open, which is what keeps a
                17-record list readable on a dashboard.

                🔴 Hideability (Reade, s199) is unchanged in kind, only in target: the condition is
                the ONE predicate in shared/constants/executor-needs-panel.ts, so switching the
                panel off later is still a single edit rather than a hunt. */}
            {isExecutorNeedsPanelVisible(user?.role) && (
              <Card data-testid="executor-needs-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{EXECUTOR_NEEDS_TITLE}</CardTitle>
                  <p className="text-xs mt-0.5 text-muted-foreground">
                    The facts a real executor would have to hunt for — each one sits on a record you
                    already created, and you can fill it in right here.
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <ExecutorNeedsPanel clientData={clientData} />
                </CardContent>
              </Card>
            )}

            {/* Estate Snapshot — full-width footer. */}
            <EstateSnapshotCard clientId={clientData.id} />
          </>
        )
      )}

      {/* s98 Build from Documents — batch upload modal (slice 2). Mounted regardless of
          the working-area gate so its open/close state is preserved across loads. */}
      {!readOnly && clientData && (
        <BatchUploadModal
          open={batchUploadOpen}
          onOpenChange={setBatchUploadOpen}
          clientData={clientData}
          onPopulated={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
            queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "estate-ready-score"] });
            queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "generic-entries"] });
            queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
            queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "section-overview"] });
          }}
        />
      )}


      {/* Government Forms Readiness card — executor view only */}
      {readOnly && formsExportData && (
        <Card className="border-l-4" style={{ borderLeftColor: 'hsl(168 76% 42%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 shrink-0" style={{ color: 'hsl(168 76% 42%)' }} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Government Forms Readiness</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formsExportData.summary.filledFields} of {formsExportData.summary.totalFields} required fields entered
                  {formsExportData.summary.missingCount === 0 ? (
                    <span className="ml-1.5 text-green-600 font-medium">· All fields complete</span>
                  ) : (
                    <span className="ml-1.5 text-amber-600 font-medium">· {formsExportData.summary.missingCount} missing</span>
                  )}
                </p>
                <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden w-48">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${formsExportData.summary.totalFields > 0 ? Math.round((formsExportData.summary.filledFields / formsExportData.summary.totalFields) * 100) : 0}%`,
                      backgroundColor: 'hsl(168 76% 42%)',
                    }}
                  />
                </div>
              </div>
              <Link href="/export/ontario-forms">
                <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                  <FileDown className="h-3.5 w-3.5" />
                  View Forms
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {!readOnly && hiddenCategories.length > 0 && (
        <Collapsible>
          <Card>
            <CardHeader className="pb-3">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                    Hidden Categories ({hiddenCategories.length})
                  </CardTitle>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [data-state=open]>*:rotate-180" />
                </div>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-2">
                <p className="text-sm text-muted-foreground mb-3">
                  These categories are hidden from your dashboard. Click the eye icon to restore them.
                </p>
                {hiddenCategories.map((hc) => {
                  const category = allCategoriesMap.get(hc.categoryId);
                  const CategoryIcon = category ? getIcon(category.icon) : null;
                  const hiddenDate = hc.hiddenAt ? new Date(hc.hiddenAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
                  return (
                    <div
                      key={hc.id}
                      className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/30"
                      data-testid={`hidden-category-${hc.categoryId}`}
                    >
                      <div className="flex items-center gap-2">
                        {CategoryIcon && <CategoryIcon className="h-4 w-4 text-primary" />}
                        <div>
                          <span className="text-sm">{category?.name || hc.categoryId}</span>
                          {hiddenDate && <p className="text-xs text-muted-foreground">Hidden on {hiddenDate}</p>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleUnhideCategory(hc.categoryId)}
                        aria-label={`Restore ${category?.name || hc.categoryId} category`}
                        data-testid={`unhide-category-${hc.categoryId}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <AddCategoryDialog
        open={addCategoryOpen}
        onOpenChange={setAddCategoryOpen}
        onSubmit={handleAddCategory}
      />

      {clientData && (
        <ClientFormDialog
          open={editClientOpen}
          onOpenChange={setEditClientOpen}
          onSubmit={(data) => updateClientMutation.mutate(data)}
          editMode={true}
          editData={clientData}
          isPending={updateClientMutation.isPending}
        />
      )}

      <DeleteConfirmDialog
        open={!!deleteCategoryTarget}
        onOpenChange={(open) => { if (!open) setDeleteCategoryTarget(null); }}
        title={`Delete "${deleteCategoryTarget?.title}"?`}
        description={
          deleteCategoryTarget?.total
            ? <span>This will permanently delete <strong>"{deleteCategoryTarget.title}"</strong> and all <strong>{deleteCategoryTarget.total} item{deleteCategoryTarget.total === 1 ? "" : "s"}</strong> inside it. This cannot be undone.</span>
            : <span>This will permanently delete <strong>"{deleteCategoryTarget?.title}"</strong> and all its items. This cannot be undone.</span>
        }
        onConfirm={() => {
          if (deleteCategoryTarget) deleteCategoryMutation.mutate(deleteCategoryTarget.id);
        }}
        isPending={deleteCategoryMutation.isPending}
        confirmLabel="Delete Category"
        testIdPrefix="category"
      />

      <DeleteConfirmDialog
        open={!!hideCategoryTarget}
        onOpenChange={(open) => { if (!open) setHideCategoryTarget(null); }}
        title={`Hide "${hideCategoryTarget?.title}"?`}
        description="This category will be hidden from your dashboard. You can restore it at any time from the Hidden Categories section at the bottom of the dashboard."
        onConfirm={() => {
          if (hideCategoryTarget) hideCategoryMutation.mutate(hideCategoryTarget.id);
        }}
        isPending={hideCategoryMutation.isPending}
        confirmLabel="Hide Category"
        cancelLabel="Keep Visible"
        testIdPrefix="hide-category"
      />
    </div>
  );
}

// ── Wizard Progress Card ──────────────────────────────────────────────────────

interface WizardStage {
  stage: number;
  title: string;
  percentComplete: number;
  completedSections: number;
  totalSections: number;
  isUnlocked: boolean;
  estimatedMinutes: number;
  previewText: string | null;
}

interface WizardProgressData {
  stages: WizardStage[];
  overallPercent: number;
  populationPercentile: number;
  currentStage: number;
  /** s178 (Ruling A): every SCORED section complete under the shared section bar, computed
   *  server-side over the bar's whole universe — not inferable from `stages`, which cover only
   *  sections carrying a `wizardStage`. Drives the plan-complete banner and the `planCompletedAt`
   *  stamp, so it is read, never re-derived. */
  planComplete?: boolean;
}


// s83: score-aware dashboard subline. "You're further along than most" reads wrong
// on a 0-essential account, so at score 0 we switch to a gentle starting line. While
// the score is still loading we keep the positive line (safer than telling an
// established planner to "start"). Score comes from useDashboardProgress (shared ERS).
// s91 (item 16): on a definitive first visit (0 essentials, not loading) we also set
// time expectations — "the essentials take about an hour" — and never show the
// comparative "further along than most" line (it reads as dishonest at 0%).
// 1F (dashboard-flow-ux.md): an adaptive greeting keyed to how many of the 10
// essentials are in place. The executor term resolves through EXECUTOR_TERM
// (jurisdiction seam) — lowercased mid-sentence in the 10/10 line.
function essentialsGreeting(score: number): string {
  if (score <= 0) return "Let's get started. Most people finish the essentials in about 30 minutes.";
  if (score <= 4) return "You're making progress. Keep going.";
  if (score <= 9) return "You're further along than most. Almost there.";
  return `The essentials are in place. Your ${EXECUTOR_TERM.toLowerCase()} is in good shape.`;
}

function DashboardSubline({ clientData }: { clientData: Client }) {
  const { isLoading, score, planComplete } = useDashboardProgress(clientData);
  // 1F greeting is suppressed until the score loads (avoids a 0/10 flash for an
  // established planner); the 1B static subtitle always renders.
  const greeting = isLoading ? null : essentialsGreeting(score);
  return (
    <>
      {greeting && (
        <p className="text-muted-foreground mt-1" data-testid="dashboard-greeting">{greeting}</p>
      )}
      {/* 1B: instructional subtitle (executor term via the jurisdiction seam).
          s176 (COMPLETE-STATE-1): this line was STATIC, so a planner with a finished
          plan was still told to "Start with Stage 1". It now has a complete state. */}
      <p className="text-sm text-muted-foreground mt-0.5">
        {planComplete
          ? `Everything is recorded. Keep it current, and your ${EXECUTOR_TERM} will have what they need.`
          : `Start with Stage 1, then work through the 10 essentials. These are what your ${EXECUTOR_TERM} will need most.`}
      </p>
    </>
  );
}

// s91 (item 14): "Welcome back" implies a return visit, which reads wrong the first
// time. On a definitive first visit (0 essentials done, not loading) greet with
// "Welcome, {name}"; otherwise "Welcome back, {name}". While loading we keep "Welcome
// back" (safer for an established planner than a fresh-sounding greeting).
function WelcomeHeading({ clientData }: { clientData: Client }) {
  const { isLoading, score } = useDashboardProgress(clientData);
  const firstName = clientData.name?.split(" ")[0] ?? "";
  const greeting = !isLoading && score === 0 ? "Welcome" : "Welcome back";
  return (
    <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: WARM.taupe }}>
      {greeting}{firstName ? `, ${firstName}` : ""}
    </p>
  );
}

/**
 * s99 Bug-2: placeholder for the dashboard working area while its shared queries load.
 * Mirrors the real layout (journey strip → build-from-docs → 2-col essentials/next-up
 * grid → snapshot footer) so the page reserves the right height and the cards swap in
 * as a unit with no layout shift. Decorative only (aria-hidden).
 */
function DashboardWorkingAreaSkeleton() {
  return (
    <div className="space-y-6" data-testid="dashboard-working-area-skeleton" aria-hidden="true">
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-6">
        <Skeleton className="h-80 w-full rounded-lg" />
        <Skeleton className="h-80 w-full rounded-lg" />
      </div>
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-28 w-full rounded-lg" />
    </div>
  );
}
